import { doc, getDoc } from 'firebase/firestore'
import { db } from '@/config/firebase'
import type { KnowledgeBaseVulnerability } from '@types'

/**
 * Knowledge Base service for fetching vulnerability metadata
 * Data is synced from Qualys KB API and stored in /knowledgeBase/{qid}/
 */
export const knowledgeBaseService = {
  /**
   * Get vulnerability metadata by QID
   */
  async getVulnerabilityByQID(qid: string | number): Promise<KnowledgeBaseVulnerability | null> {
    const qidString = String(qid)
    const kbRef = doc(db, 'knowledgeBase', qidString)

    const snapshot = await getDoc(kbRef)

    if (!snapshot.exists()) {
      return null
    }

    const data = snapshot.data()

    return {
      qid: data.qid,
      title: data.title,
      category: data.category,
      vulnType: data.vulnType,
      severityLevel: data.severityLevel,
      published: data.published?.toDate?.(),
      modified: data.modified?.toDate?.(),
      cvssScore: data.cvssScore,
      cvssTemporal: data.cvssTemporal,
      cvssVector: data.cvssVector,
      cvssV3Score: data.cvssV3Score,
      cvssV3Temporal: data.cvssV3Temporal,
      cvssV3Vector: data.cvssV3Vector,
      cvssV3Version: data.cvssV3Version,
      threatIntel: data.threatIntel,
      patchable: data.patchable,
      patchPublishedDate: data.patchPublishedDate?.toDate?.(),
      diagnosis: data.diagnosis,
      consequence: data.consequence,
      solution: data.solution,
      complianceList: data.complianceList,
      cveList: data.cveList,
      vendorReferenceList: data.vendorReferenceList,
      bugtraqList: data.bugtraqList,
      softwareList: data.softwareList,
    } as KnowledgeBaseVulnerability
  },

  /**
   * Get vulnerability metadata for multiple QIDs
   * Returns a Map of QID -> KnowledgeBaseVulnerability
   *
   * Uses direct document fetches by ID instead of field queries
   * to avoid string/number type mismatches in the qid field
   */
  async getVulnerabilitiesByQIDs(
    qids: (string | number)[]
  ): Promise<Map<string, KnowledgeBaseVulnerability>> {
    const result = new Map<string, KnowledgeBaseVulnerability>()

    if (qids.length === 0) {
      return result
    }

    // Fetch documents directly by ID (bypasses field type issues)
    // Process in parallel for performance
    const fetchPromises = qids.map(async (qid) => {
      const qidString = String(qid)
      const kbRef = doc(db, 'knowledgeBase', qidString)

      try {
        const snapshot = await getDoc(kbRef)

        if (snapshot.exists()) {
          const data = snapshot.data()
          result.set(qidString, {
            qid: data.qid,
            title: data.title,
            category: data.category,
            vulnType: data.vulnType,
            severityLevel: data.severityLevel,
            published: data.published?.toDate?.(),
            modified: data.modified?.toDate?.(),
            cvssScore: data.cvssScore,
            cvssTemporal: data.cvssTemporal,
            cvssVector: data.cvssVector,
            cvssV3Score: data.cvssV3Score,
            cvssV3Temporal: data.cvssV3Temporal,
            cvssV3Vector: data.cvssV3Vector,
            cvssV3Version: data.cvssV3Version,
            threatIntel: data.threatIntel,
            patchable: data.patchable,
            patchPublishedDate: data.patchPublishedDate?.toDate?.(),
            diagnosis: data.diagnosis,
            consequence: data.consequence,
            solution: data.solution,
            complianceList: data.complianceList,
            cveList: data.cveList,
            vendorReferenceList: data.vendorReferenceList,
            bugtraqList: data.bugtraqList,
            softwareList: data.softwareList,
          } as KnowledgeBaseVulnerability)
        }
      } catch (error) {
        // Log but don't fail - just skip this QID
        console.warn(`[KB] Failed to fetch QID ${qid}:`, error)
      }
    })

    await Promise.all(fetchPromises)

    return result
  },

  /**
   * Parse CVSS v3 score into severity category
   */
  getCVSSSeverity(cvssScore: string | number | undefined): 'critical' | 'high' | 'medium' | 'low' | 'none' {
    if (!cvssScore) return 'none'

    const score = typeof cvssScore === 'string' ? parseFloat(cvssScore) : cvssScore

    if (score >= 9.0) return 'critical'
    if (score >= 7.0) return 'high'
    if (score >= 4.0) return 'medium'
    if (score >= 0.1) return 'low'
    return 'none'
  },
}
